import React, { useState } from 'react'
import { useQuery, useMutation } from 'urql'
import { GET_USERS, UPDATE_USER_STATUS, UPDATE_USER_ROLES, TOGGLE_USER_ACTIVE, DELETE_USER, GET_DELETED_USERS_HISTORY } from '../../graphql/users'

function StatusBadge({ status }) {
  const isActive = status !== 'DESACTIVADO'
  return (
    <span className={`status-badge ${isActive ? 'activo' : 'desactivado'}`}>
      {isActive ? 'Activo' : 'Desactivado'}
    </span>
  )
}

function RoleBadge({ roles }) {
  if (!roles || roles.length === 0) return <span className="role-badge user">Usuario</span>
  
  if (roles.includes('ADMIN')) return <span className="role-badge admin">Admin</span>
  if (roles.includes('DPO')) return <span className="role-badge admin">DPO</span>
  if (roles.includes('SUPER_COLABORADOR')) return <span className="role-badge colaborador">Super Colaborador</span>
  if (roles.includes('COLABORADOR')) return <span className="role-badge colaborador">Colaborador</span>
  return <span className="role-badge user">Usuario</span>
}

function RolesModal({ user, onClose, onConfirm, isBulk = false, selectedCount = 0 }) {
  const availableRoles = [
    { value: 'ADMIN', label: 'Admin' },
    { value: 'DPO', label: 'DPO' },
    { value: 'SUPER_COLABORADOR', label: 'Super Colaborador' },
    { value: 'COLABORADOR', label: 'Colaborador' },
    { value: 'USER_SMARTCFO', label: 'Usuario' },
  ]

  const [selectedRoles, setSelectedRoles] = useState(user?.roles || [])

  const handleToggleRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onConfirm(user?._id, selectedRoles)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {isBulk 
              ? `Editar Roles - ${selectedCount} usuarios seleccionados`
              : `Editar Roles - ${user?.email}`
            }
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Roles Disponibles</label>
              <div className="roles-grid">
                {availableRoles.map((role) => (
                  <label
                    key={role.value}
                    className={`role-option ${selectedRoles.includes(role.value) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role.value)}
                      onChange={() => handleToggleRole(role.value)}
                    />
                    <span>{role.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {isBulk ? `Aplicar a ${selectedCount} usuarios` : 'Guardar Roles'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function UsersList({ onSelectUser }) {
  const [filters, setFilters] = useState({
    search: '',
    adminStatus: undefined,
    role: undefined,
    page: 1,
    pageSize: 20,
  })
  const [rolesModal, setRolesModal] = useState(null)
  const [bulkRolesModal, setBulkRolesModal] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)

  // Construir filtros solo con valores definidos
  const cleanFilters = {}
  if (filters.search?.trim()) {
    cleanFilters.search = filters.search.trim()
  }
  if (filters.adminStatus) {
    cleanFilters.adminStatus = filters.adminStatus
  }
  if (filters.role) {
    cleanFilters.role = filters.role
  }
  if (filters.page) {
    cleanFilters.page = filters.page
  }
  if (filters.pageSize) {
    cleanFilters.pageSize = filters.pageSize
  }

  const [result, reexecuteQuery] = useQuery({
    query: GET_USERS,
    variables: { filter: cleanFilters },
    requestPolicy: 'network-only',
  })

  // updateStatus se usará para cambiar el estado del usuario
  // eslint-disable-next-line no-unused-vars
  const [, updateStatus] = useMutation(UPDATE_USER_STATUS)
  const [, updateRoles] = useMutation(UPDATE_USER_ROLES)
  const [, toggleActive] = useMutation(TOGGLE_USER_ACTIVE)
  const [, deleteUser] = useMutation(DELETE_USER)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data, fetching, error } = result
  const users = data?.users?.items || []
  const pagination = data?.users || { totalCount: 0, page: 1, totalPages: 1 }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      page: 1,
    }))
    setSelectedIds(new Set())
  }

  const handleRolesChange = async (userId, newRoles) => {
    await updateRoles({
      input: { userId, roles: newRoles },
    })
    setRolesModal(null)
    reexecuteQuery({ requestPolicy: 'network-only' })
  }

  const handleToggleActive = async (user) => {
    const isActive = user.adminStatus !== 'DESACTIVADO'
    const confirmMessage = isActive
      ? `¿Desactivar al usuario "${user.email}"?`
      : `¿Activar al usuario "${user.email}"?`

    if (window.confirm(confirmMessage)) {
      await toggleActive({
        input: {
          userId: user._id,
          isActive: !isActive,
        },
      })
      reexecuteQuery({ requestPolicy: 'network-only' })
    }
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(users.map((u) => u._id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const handleBulkRolesChange = async (_, newRoles) => {
    setBulkLoading(true)
    try {
      const promises = Array.from(selectedIds).map((userId) =>
        updateRoles({ input: { userId, roles: newRoles } })
      )
      await Promise.all(promises)
      setBulkRolesModal(false)
      setSelectedIds(new Set())
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (err) {
      alert('Error al aplicar cambios masivos')
    }
    setBulkLoading(false)
  }

  const handleBulkToggleActive = async (activate) => {
    const action = activate ? 'activar' : 'desactivar'
    const confirmMessage = `¿${action.charAt(0).toUpperCase() + action.slice(1)} ${selectedIds.size} usuarios seleccionados?`

    if (window.confirm(confirmMessage)) {
      setBulkLoading(true)
      try {
        const promises = Array.from(selectedIds).map((userId) =>
          toggleActive({
            input: {
              userId,
              isActive: activate,
              reason: `${activate ? 'Activado' : 'Desactivado'} masivamente desde panel admin`,
            },
          })
        )
        await Promise.all(promises)
        setSelectedIds(new Set())
        reexecuteQuery({ requestPolicy: 'network-only' })
      } catch (err) {
        alert('Error al aplicar cambios masivos')
      }
      setBulkLoading(false)
    }
  }

  const handleDeleteUser = async (user) => {
    const reason = prompt(`¿Por qué deseas eliminar a "${user.email}"?\n(Este registro quedará en el historial)`)
    if (reason === null) return
    try {
      const result = await deleteUser({
        input: { userId: user._id, reason: reason || 'Sin motivo especificado' },
      })
      if (result.error) {
        alert('Error al eliminar: ' + result.error.message)
        return
      }
      setDeleteConfirm(null)
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (err) {
      alert('Error al eliminar usuario')
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('es-CL')
  }

  const isAllSelected = users.length > 0 && selectedIds.size === users.length

  if (error) {
    return (
      <div className="content-card">
        <div className="card-body">
          <div className="empty-state">
            <p>Error al cargar usuarios: {error.message}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Usuarios</h1>
        <p className="page-subtitle">Gestión de usuarios del sistema</p>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">Lista de Usuarios</h3>
          {selectedIds.size > 0 && (
            <div className="bulk-actions">
              <span className="bulk-count">{selectedIds.size} seleccionados</span>
              <button
                className="btn btn-warning btn-sm"
                onClick={() => setBulkRolesModal(true)}
                disabled={bulkLoading}
              >
                Editar Roles
              </button>
              <button
                className="btn btn-success btn-sm"
                onClick={() => handleBulkToggleActive(true)}
                disabled={bulkLoading}
              >
                Activar
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleBulkToggleActive(false)}
                disabled={bulkLoading}
              >
                Desactivar
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
        <div className="card-body">
          {/* Filtros mejorados */}
          <div className="filters-container">
            <div className="filter-group">
              <label className="filter-label">BUSCAR</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Email, nombre..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">ESTADO</label>
              <select
                className="filter-select"
                value={filters.adminStatus || ''}
                onChange={(e) => handleFilterChange('adminStatus', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="ACTIVO">Activo</option>
                <option value="DESACTIVADO">Desactivado</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">ROL</label>
              <select
                className="filter-select"
                value={filters.role || ''}
                onChange={(e) => handleFilterChange('role', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="ADMIN">Admin</option>
                <option value="DPO">DPO</option>
                <option value="SUPER_COLABORADOR">Super Colaborador</option>
                <option value="COLABORADOR">Colaborador</option>
                <option value="USER_SMARTCFO">Usuario</option>
              </select>
            </div>
          </div>

          {/* Tabla */}
          {fetching ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <p>No se encontraron usuarios</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      title="Seleccionar todos"
                    />
                  </th>
                  <th>USUARIO</th>
                  <th>ROL</th>
                  <th>ESTADO</th>
                  <th>ADMIN SCFO</th>
                  <th>CREADO</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr 
                    key={user._id}
                    className={`clickable-row ${selectedIds.has(user._id) ? 'selected-row' : ''}`}
                    onClick={() => onSelectUser(user._id)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(user._id)}
                        onChange={() => handleSelectOne(user._id)}
                      />
                    </td>
                    <td>
                      <div className="user-email">{user.email}</div>
                      {(user.firstName || user.lastName) && (
                        <div className="user-name">{user.firstName} {user.lastName}</div>
                      )}
                    </td>
                    <td>
                      <RoleBadge roles={user.roles} />
                    </td>
                    <td>
                      <StatusBadge status={user.adminStatus || 'ACTIVO'} />
                    </td>
                    <td className="text-center">
                      {user.isSmartCfoAdmin ? (
                        <span className="admin-indicator">Sí</span>
                      ) : (
                        <span className="text-muted">No</span>
                      )}
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => setRolesModal(user)}
                        >
                          Roles
                        </button>
                        {user.adminStatus !== 'DESACTIVADO' ? (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleToggleActive(user)}
                          >
                            Desactivar
                          </button>
                        ) : (
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleToggleActive(user)}
                          >
                            Activar
                          </button>
                        )}
                        {deleteConfirm === user._id ? (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteUser(user)}
                            onBlur={() => setTimeout(() => setDeleteConfirm(null), 200)}
                          >
                            ¿Confirmar?
                          </button>
                        ) : (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => setDeleteConfirm(user._id)}
                            title="Eliminar usuario"
                            style={{ padding: '0.35rem 0.5rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-info">
              Mostrando {users.length} de {pagination.totalCount} usuarios
            </div>
            <div className="pagination-controls">
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                ← Anterior
              </button>
              <span className="pagination-pages">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de edición de roles individual */}
      {rolesModal && (
        <RolesModal
          user={rolesModal}
          onClose={() => setRolesModal(null)}
          onConfirm={handleRolesChange}
        />
      )}

      {/* Modal de edición de roles masivo */}
      {bulkRolesModal && (
        <RolesModal
          user={null}
          onClose={() => setBulkRolesModal(false)}
          onConfirm={handleBulkRolesChange}
          isBulk={true}
          selectedCount={selectedIds.size}
        />
      )}

      {/* Historial de Usuarios Eliminados */}
      <DeletedUsersHistory />
    </>
  )
}

// Componente para mostrar historial de usuarios eliminados
function DeletedUsersHistory() {
  const [isExpanded, setIsExpanded] = useState(false)
  
  const [result] = useQuery({
    query: GET_DELETED_USERS_HISTORY,
    variables: { filter: { page: 1, pageSize: 50 } },
    pause: !isExpanded,
    requestPolicy: 'network-only',
  })

  const { data, fetching } = result
  const deletedUsers = data?.deletedUsersHistory?.items || []

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="content-card" style={{ marginTop: '1.5rem' }}>
      <div 
        className="card-header" 
        style={{ cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="card-title">
          {isExpanded ? '▼' : '▶'} Historial de Usuarios Eliminados
        </h3>
        <span style={{ color: '#7f8c8d', fontSize: '0.875rem' }}>
          {isExpanded ? 'Clic para ocultar' : 'Clic para ver'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="card-body">
          {fetching ? (
            <div className="loading-small">Cargando historial...</div>
          ) : deletedUsers.length === 0 ? (
            <div className="empty-state-small">
              <span>No hay usuarios eliminados registrados</span>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Rol</th>
                  <th>Fecha de Eliminación</th>
                  <th>Eliminado por</th>
                  <th>Razón</th>
                </tr>
              </thead>
              <tbody>
                {deletedUsers.map((log) => (
                  <tr key={log._id}>
                    <td>
                      <div className="user-email">
                        {log.snapshot?.email || 'Email no disponible'}
                      </div>
                    </td>
                    <td>
                      <div className="user-name">
                        {log.snapshot?.firstName || ''} {log.snapshot?.lastName || ''}
                      </div>
                    </td>
                    <td>
                      <RoleBadge roles={log.snapshot?.roles || []} />
                    </td>
                    <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {formatDateTime(log.performedAt)}
                    </td>
                    <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {log.performedByEmail}
                    </td>
                    <td style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: log.reason ? 'normal' : 'italic' }}>
                      {log.reason || 'Sin razón especificada'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

export default UsersList
